const COBALT_LINK_SCRIPT_ID = 'cobalt-link-script'
const COBALT_LINK_SCRIPT_URL = 'https://link.usecobalt.com/init.js'

interface CobaltLinkOptions {
  token: string
  onSuccess: (publicToken: string) => void
}

declare global {
  interface Window {
    CobaltLink?: {
      init: (options: CobaltLinkOptions) => void
    }
  }
}

let cobaltLinkPromise: Promise<void> | undefined

function loadCobaltLink(): Promise<void> {
  if (window.CobaltLink) return Promise.resolve()
  if (cobaltLinkPromise) return cobaltLinkPromise

  cobaltLinkPromise = new Promise((resolve, reject) => {
    // A failed attempt leaves a dead <script> in the head. Reusing that element
    // would attach listeners to something the browser never fetches again, so
    // the retry's promise would never settle and every later Connect click
    // would burn a one-time link token on a silent hang. Always start from a
    // fresh element, and clear the dead one on the way out.
    document.getElementById(COBALT_LINK_SCRIPT_ID)?.remove()
    const script = document.createElement('script')

    const fail = (message: string) => {
      cobaltLinkPromise = undefined
      script.remove()
      reject(new Error(message))
    }

    const onLoad = () => {
      if (window.CobaltLink) {
        resolve()
      } else {
        fail('Cobalt Link did not initialize')
      }
    }
    const onError = () => {
      fail('Cobalt Link could not be loaded')
    }

    script.addEventListener('load', onLoad, { once: true })
    script.addEventListener('error', onError, { once: true })
    script.id = COBALT_LINK_SCRIPT_ID
    script.src = COBALT_LINK_SCRIPT_URL
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  })

  return cobaltLinkPromise
}

export async function launchCobaltLink(
  token: string,
  onSuccess: (publicToken: string) => void | Promise<void>
): Promise<void> {
  await loadCobaltLink()
  window.CobaltLink!.init({
    token,
    onSuccess: (publicToken) => {
      void onSuccess(publicToken)
    },
  })
}
